import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { markAbsent } from '@/services/attendanceService';
import { AbsentReason, ABSENT_REASON_LABELS } from '@/types/attendance';
import { Loader2 } from 'lucide-react';

interface AbsentReasonDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  userName: string;
  officeId: string;
  officeName: string;
  onAbsentMarked: () => void;
}

export function AbsentReasonDialog({
  open,
  onOpenChange,
  userId,
  userName,
  officeId,
  officeName,
  onAbsentMarked,
}: AbsentReasonDialogProps) {
  const [reason, setReason] = useState<AbsentReason | ''>('');
  const [remarks, setRemarks] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!reason) {
      return;
    }

    setSubmitting(true);

    try {
      await markAbsent(userId, userName, officeId, officeName, reason, remarks);
      onAbsentMarked();
      onOpenChange(false);
      
      // Reset form
      setReason('');
      setRemarks('');
    } catch (error) {
      console.error('Failed to mark absent:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
    // Reset form
    setReason('');
    setRemarks('');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Mark Absent</DialogTitle>
          <DialogDescription>
            Select absence reason
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="reason">Reason *</Label>
            <Select
              value={reason}
              onValueChange={(value) => setReason(value as AbsentReason)}
            >
              <SelectTrigger id="reason" autoFocus>
                <SelectValue placeholder="Select a reason" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(ABSENT_REASON_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="remarks">Remarks (Optional)</Label>
            <Textarea
              id="remarks"
              placeholder="Additional details (optional)..."
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              rows={2}
              className="resize-none"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            onClick={handleSubmit}
            disabled={!reason || submitting}
            className="bg-red-600 hover:bg-red-700"
          >
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Mark Absent
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
